import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import AccessRestrictedPage from "../pages/AccessRestrictedPage";

// Roles whose dashboard is meaningless without an organisation linked.
// If one of these signs up and reaches a protected page WITHOUT an
// organization_id, we bounce them through the self-service onboarding
// wizard.  This closes the Feb 2026 production multi-tenant leak where
// a brand-new org_admin was seeing other tenants' data.
const ORG_REQUIRED_ROLES = new Set(["org_admin", "event_manager"]);

export default function ProtectedRoute({ children, roles, allowMissingOrg = false }) {
  const { user, loading } = useAuth();
  const loc = useLocation();

  if (loading || user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex items-center gap-3 text-[#0A192F]">
          <div className="h-3 w-3 rounded-full bg-[#D4AF37] animate-pulse" />
          <span className="font-medium tracking-wide">Loading Diplomexa…</span>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: loc }} replace />;
  }

  // Organisation gate — must run BEFORE the role check so an org-less
  // organiser gets sent to onboarding instead of bouncing to /dashboard.
  if (
    !allowMissingOrg
    && ORG_REQUIRED_ROLES.has(user.role)
    && !user.organization_id
    && loc.pathname !== "/onboarding/organisation"
  ) {
    return <Navigate to="/onboarding/organisation" replace />;
  }

  // Sprint RBAC-X Slice 1: show a proper <AccessRestricted> page on
  // role-mismatch instead of silently bouncing to /dashboard.  Users
  // get clear feedback that the URL is blocked, and unauthorised URL
  // tampering no longer looks like a routing bug.
  if (roles && !roles.includes(user.role) && user.role !== "super_admin") {
    return <AccessRestrictedPage allowedRoles={roles} userRole={user.role} />;
  }

  return children;
}
